import { useState, useMemo, useEffect, useContext, useReducer } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import './stylesSetPassword.css';
import { NAVIGATION_ROUTES } from '../../utils/NavigationUtils';
import { message } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import PublicAdminPanelInstance from '../../api/AffiliatePanelInstance';
import { API_ROUTES } from '../../api/ApiRoutes';
import { AppStrings } from '../../utils/constants/AppStrings';
import PasswordPolicyChecklist from '../shared/PasswordPolicyChecklist/PasswordPolicyChecklist';
import { usePasswordPolicyValidation } from '../../hooks/usePasswordPolicyValidation';
import { AUTH_AUDIENCES, AUTH_AUDIENCE_VALUES } from '../../utils/auth/authAudience';
import { AFFILIATE_PORTAL_SIGN_IN_ROUTE } from '../../routing/affiliatePortalRoutes';
import { REGULATOR_PORTAL_SIGN_IN_ROUTE } from '../RegulatorPortal/regulatorPortalRoutes';
import AppContext from '../../context/AppContext';
import {
  AUTH_FLOW_ACTIONS,
  AUTH_FLOW_STEPS,
  AUTH_RESPONSE_TYPES,
  authFlowReducer,
  classifyAuthResponse,
  createInitialAuthFlowState,
} from '../../utils/auth/authFlow';
import {
  associatePamMfa,
  verifyPamMfaEnrollment,
} from '../../queries/auth/pamMfaClient';
import {
  PAM_MFA_ERROR_CODES,
  buildPamTotpUri,
  getPamMfaErrorCode,
  getPamMfaErrorMessage,
} from '../../utils/auth/pamMfa';
import { finalizeAuthenticatedSession } from '../../services/authSessionService';
import { useIpWhitelistAccess } from '../IpWhitelistGate/IpWhitelistGate';
import AuthenticatorEnrollment from '../LogIn/TwoFactor/AuthenticatorEnrollment';

const getSignInRoute = (audience) => {
  if (audience === AUTH_AUDIENCES.AFFILIATE) {
    return AFFILIATE_PORTAL_SIGN_IN_ROUTE;
  }
  if (audience === AUTH_AUDIENCES.REGULATOR) {
    return REGULATOR_PORTAL_SIGN_IN_ROUTE;
  }
  return NAVIGATION_ROUTES?.LOGIN;
};

function SetPassword({ audience }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { setToken, setLastLogInTimestamp } = useContext(AppContext) || {};
  const { blockAccess } = useIpWhitelistAccess();
  const { username, sessionToken } = location.state || {};
  const effectiveAudience =
    audience ?? location.state?.audience ?? AUTH_AUDIENCES.STAFF;
  const signInRoute = getSignInRoute(effectiveAudience);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInlinePolicyError, setShowInlinePolicyError] = useState(false);
  const [authFlowState, dispatchAuthFlow] = useReducer(
    authFlowReducer,
    undefined,
    createInitialAuthFlowState,
  );

  // Redirect to login if reached without challenge (e.g. direct URL)
  useEffect(() => {
    if (!username || !sessionToken) {
      message.warning(AppStrings.PAM_SET_PASSWORD_SIGN_IN_FIRST);
      navigate(signInRoute, { replace: true });
    }
  }, [username, sessionToken, navigate, signInRoute]);

  useEffect(() => {
    if (
      authFlowState.step !== AUTH_FLOW_STEPS.TOTP_ENROLLMENT ||
      !authFlowState.expiresIn
    ) {
      return undefined;
    }

    const expirationTimer = window.setTimeout(() => {
      dispatchAuthFlow({ type: AUTH_FLOW_ACTIONS.RESET });
      message.error(AppStrings.PAM_2FA_ENROLLMENT_TICKET_EXPIRED);
      navigate(signInRoute, { replace: true });
    }, authFlowState.expiresIn * 1000);

    return () => window.clearTimeout(expirationTimer);
  }, [
    authFlowState.expiresIn,
    authFlowState.step,
    navigate,
    signInRoute,
  ]);

  const affiliatePasswordValidation = useMemo(() => ({
    hasMinLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSymbol: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
  }), [password]);

  const usesBackendPolicy = effectiveAudience !== AUTH_AUDIENCES.AFFILIATE;
  const passwordPolicy = usePasswordPolicyValidation({
    email: username,
    password,
    audience:
      effectiveAudience === AUTH_AUDIENCES.REGULATOR ? 'regulator' : 'staff',
    enabled: usesBackendPolicy,
  });
  const isPasswordValid = usesBackendPolicy
    ? passwordPolicy.allPassed
    : Object.values(affiliatePasswordValidation).every(Boolean);
  const hasChallengeContext = Boolean(username && sessionToken);
  const isButtonDisabled = isSubmitting || !isPasswordValid || confirmPassword !== password || !hasChallengeContext;

  const beginTotpEnrollment = async ({
    enrollmentTicket,
    expiresIn,
  }) => {
    const response = await associatePamMfa(enrollmentTicket);
    const secret = response?.data?.secret_code;
    const associatedUsername = response?.data?.username;
    if (
      typeof secret !== 'string' ||
      !secret ||
      typeof associatedUsername !== 'string' ||
      !associatedUsername
    ) {
      throw new Error('Malformed MFA association response');
    }

    setPassword('');
    setConfirmPassword('');
    dispatchAuthFlow({
      type: AUTH_FLOW_ACTIONS.REQUIRE_TOTP_ENROLLMENT,
      username,
      enrollmentTicket,
      expiresIn,
      secret,
      otpauthUri: buildPamTotpUri({
        issuer: AppStrings.PAM_APP_NAME,
        username: associatedUsername,
        secret,
      }),
    });
  };

  const handleEnrollmentVerification = async (code) => {
    const response = await verifyPamMfaEnrollment({
      enrollmentTicket: authFlowState.enrollmentTicket,
      code,
    });
    const result = classifyAuthResponse(response?.data);
    if (result.type !== AUTH_RESPONSE_TYPES.FINAL_TOKENS) {
      throw new Error('Malformed final authentication response');
    }

    dispatchAuthFlow({ type: AUTH_FLOW_ACTIONS.RESET });
    await finalizeAuthenticatedSession({
      token: result.token,
      email: username,
      setToken,
      setLastLogInTimestamp,
      dispatch,
      navigate,
      onIpBlocked: blockAccess,
      audience: AUTH_AUDIENCES.STAFF,
    });
  };

  const leaveEnrollmentForSignIn = (errorMessage) => {
    dispatchAuthFlow({ type: AUTH_FLOW_ACTIONS.RESET });
    if (errorMessage) {
      message.error(errorMessage);
    }
    navigate(signInRoute, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !sessionToken) return;

    setIsSubmitting(true);

    if (!password || !confirmPassword) {
      message.error(AppStrings.PAM_PASSWORD_FIELDS_REQUIRED);
      setIsSubmitting(false);
      return;
    }

    if (!isPasswordValid) {
      message.error(AppStrings.PAM_PASSWORD_REQUIREMENTS_NOT_MET);
      setIsSubmitting(false);
      return;
    }

    const payload = {
      user_name: username,
      session: sessionToken,
      new_password: password,
    };

    try {
      const setPasswordRoute =
        effectiveAudience === AUTH_AUDIENCES.AFFILIATE
          ? API_ROUTES.AFFILIATE_PORTAL_SET_PASSWORD
          : API_ROUTES.SET_PASSWORD;
      const response = await PublicAdminPanelInstance.post(
        setPasswordRoute,
        payload,
      );
      if (effectiveAudience === AUTH_AUDIENCES.STAFF) {
        const responseData = response?.data?.data;
        const result = classifyAuthResponse(responseData);
        if (result.type === AUTH_RESPONSE_TYPES.TOTP_ENROLLMENT_REQUIRED) {
          await beginTotpEnrollment({
            enrollmentTicket: result.enrollmentTicket,
            expiresIn: result.expiresIn,
          });
          return;
        }
      }
      message.success(AppStrings.PAM_PASSWORD_CREATED_SUCCESS);
      navigate(signInRoute, { replace: true });
    } catch (error) {
      // Staff /set-password fails with InvalidParameterException when the user is
      // actually an affiliate — same Cognito client/session, but the affiliate pool
      // expects different user attributes. Retry the same payload against the
      // affiliate endpoint; the affiliate-group check there decides who's allowed.
      const staffData = error?.response?.data;
      if (
        usesBackendPolicy &&
        staffData?.error_code === 'INVALID_PASSWORD' &&
        passwordPolicy.applyPolicyResult(staffData?.detail)
      ) {
        setShowInlinePolicyError(true);
        return;
      }
      const mfaErrorCode = getPamMfaErrorCode(error);
      if (mfaErrorCode) {
        const mfaMessage = getPamMfaErrorMessage(error);
        if (
          mfaErrorCode ===
          PAM_MFA_ERROR_CODES.ENROLLMENT_TICKET_EXPIRED
        ) {
          leaveEnrollmentForSignIn(mfaMessage);
        } else {
          message.error(mfaMessage);
        }
        return;
      }
      const isAffiliateRetryCandidate =
        effectiveAudience === AUTH_AUDIENCES.STAFF &&
        staffData?.error_code === 'SET_PASSWORD_FAILED' &&
        staffData?.detail?.cognito_error === 'InvalidParameterException';

      if (isAffiliateRetryCandidate) {
        try {
          await PublicAdminPanelInstance.post(API_ROUTES?.AFFILIATE_PORTAL_SET_PASSWORD, payload);
          message.success(AppStrings.PAM_PASSWORD_CREATED_SUCCESS);
          navigate(AFFILIATE_PORTAL_SIGN_IN_ROUTE, { replace: true });
          return;
        } catch (affiliateError) {
          const affiliateData = affiliateError?.response?.data;
          const affiliateCode = affiliateData?.error_code;
          // Surface the affiliate message when it's more actionable than the
          // generic staff "Failed to set new password". Otherwise fall back to
          // the original staff message so we don't paper over a real issue.
          const useAffiliateMessage =
            affiliateCode === 'INVALID_PASSWORD' || affiliateCode === 'SESSION_EXPIRED';
          message.error(
            (useAffiliateMessage && affiliateData?.message) ||
              staffData?.message ||
              affiliateData?.message ||
              AppStrings.PAM_SET_PASSWORD_FAILED
          );
          return;
        }
      }

      message.error(staffData?.message || AppStrings.PAM_SET_PASSWORD_FAILED);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasChallengeContext) {
    return null;
  }

  if (authFlowState.step === AUTH_FLOW_STEPS.TOTP_ENROLLMENT) {
    return (
      <div className="set-password-container set-password-container--mfa">
        <AuthenticatorEnrollment
          secret={authFlowState.secret}
          otpauthUri={authFlowState.otpauthUri}
          onVerify={handleEnrollmentVerification}
          onCancel={() => leaveEnrollmentForSignIn()}
          onExpired={leaveEnrollmentForSignIn}
        />
      </div>
    );
  }

  return (
    <div className="set-password-container">
      <form className="set-password-card" onSubmit={handleSubmit}>
        <div className="set-password-card-header">
          <h1>{AppStrings.PAM_SET_PASSWORD_TITLE}</h1>
          <p>{AppStrings.PAM_SET_PASSWORD_SUBTITLE}</p>
        </div>
        <div className="form-group">
          <label>{AppStrings.PASSWORD}</label>
          <input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setShowInlinePolicyError(false);
            }}
            placeholder={AppStrings.PAM_SET_PASSWORD_PLACEHOLDER}
          />
          {usesBackendPolicy ? (
            <PasswordPolicyChecklist
              result={passwordPolicy.policyResult}
              isCurrent={passwordPolicy.isCurrent}
              isPending={passwordPolicy.isPending}
              hasError={passwordPolicy.hasError}
              showInlineError={showInlinePolicyError}
            />
          ) : (
            <div className="password-requirements">
              <p className="requirements-title">
                {AppStrings.PAM_PASSWORD_REQUIREMENTS_TITLE}
              </p>
              <ul>
                <li data-testid="req-length" className={affiliatePasswordValidation.hasMinLength ? 'valid' : 'invalid'}>
                  <CheckCircleOutlined /> {AppStrings.PAM_AFFILIATE_PASSWORD_MIN_LENGTH}
                </li>
                <li data-testid="req-uppercase" className={affiliatePasswordValidation.hasUppercase ? 'valid' : 'invalid'}>
                  <CheckCircleOutlined /> {AppStrings.PAM_PASSWORD_UPPERCASE}
                </li>
                <li data-testid="req-number" className={affiliatePasswordValidation.hasNumber ? 'valid' : 'invalid'}>
                  <CheckCircleOutlined /> {AppStrings.PAM_PASSWORD_NUMBER}
                </li>
                <li data-testid="req-symbol" className={affiliatePasswordValidation.hasSymbol ? 'valid' : 'invalid'}>
                  <CheckCircleOutlined /> {AppStrings.PAM_AFFILIATE_PASSWORD_SYMBOL}
                </li>
              </ul>
            </div>
          )}
        </div>

        <div className="form-group">
          <label>{AppStrings.PAM_SET_PASSWORD_CONFIRM_LABEL}</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={AppStrings.PAM_SET_PASSWORD_CONFIRM_PLACEHOLDER}
          />
        </div>

        <button type="submit" disabled={isButtonDisabled} className={`set-password-button ${isButtonDisabled ? 'disabled' : ''}`}>
          {isSubmitting ? AppStrings.SAVING : AppStrings.SAVE}
        </button>
      </form>
    </div>
  );
}

SetPassword.propTypes = {
  audience: PropTypes.oneOf(AUTH_AUDIENCE_VALUES),
};

export default SetPassword;
